import Vue from 'vue';
import type { DirectiveBinding, DirectiveOptions } from 'vue/types/options';
import {
    ImageCdnUrlResolver,
    type ImageCacheMode,
    type ImageCdnResolveOptions,
} from '@/utils/image/image-resolver.utils';
import {
    IMAGE_LOADING,
    type LazyImageDirectiveBinding,
} from '@/types/components/image-loading.types';

const DEFAULT_IMAGE_FORMAT = '';
const DEFAULT_IMAGE_QUALITY = 80;
const DEFAULT_IMAGE_VERSION = '';
const DEFAULT_IMAGE_WIDTH: string | number | '' = '';
const DEFAULT_IMAGE_CACHE_CONTROLLER_PATH = '/Image/GetCache';
const DEFAULT_IMAGE_CACHE_CONTROLLER_PATH_PATTERNS = [
    '^(Images/.+|Content/doc/.+)\\.(png|jpg|jpeg)$',
];

type BindingInput = string | LazyImageDirectiveBinding;

interface NormalizedBinding {
    src: string;
    tag: string;
    format?: string;
    quality?: string | number;
    version?: string | number | boolean;
    width?: string | number;
    loading: IMAGE_LOADING;
    fetchPriority?: LazyImageDirectiveBinding['fetchPriority'];
    cdnPrefixes: string[];
}

type LazyElementState = {
    requestId: number;
    sourcePath: string;
    finalUrl: string;
    candidateIndex: number;
};

type PlaceholderTagConfig = {
    error: string;
    cdnPrefixes: string[];
    transform: ((url: string) => string) | null;

    format: string;
    quality: string | number | '';
    version: string | number | boolean | '';
    width: string | number | '';
};

type PlaceholderOptions = Record<string, PlaceholderTagConfig>;

type PlaceholderOptionInput = Omit<Partial<PlaceholderTagConfig>, 'format'> & {
    transform?: ((url: string) => string) | null;
    format?: string | false;
};

type LazyLoaderOptions = {
    cdnPrefixes?: string[];
    transform?: ((url: string) => string) | null;

    imageCacheMode?: ImageCacheMode;
    imageCacheControllerPath?: string;
    imageCacheControllerPathPatterns?: string[];

    placeholderOptions?: Record<string, PlaceholderOptionInput>;
};

/**
 * 圖片 lazy loader (Vue 指令 + DOM hydration)
 */
export class LazyLoader {
    private readonly placeholderOptions: PlaceholderOptions;

    private readonly cdnResolverMap = new Map<string, ImageCdnUrlResolver>();

    private readonly imageCacheMode: ImageCacheMode;

    private readonly imageCacheControllerPath: string;

    private readonly imageCacheControllerPathPatterns: string[];

    private directiveName: string | null = null;

    private imageRequestId = 0;

    private readonly elementStateMap = new WeakMap<
        HTMLImageElement,
        LazyElementState
    >();

    private readonly elementObserverMap = new WeakMap<
        HTMLImageElement,
        IntersectionObserver
    >();

    constructor(options: LazyLoaderOptions = {}) {
        this.imageCacheMode = options.imageCacheMode || 'rewrite';
        this.imageCacheControllerPath =
            options.imageCacheControllerPath ||
            DEFAULT_IMAGE_CACHE_CONTROLLER_PATH;
        this.imageCacheControllerPathPatterns = Array.isArray(
            options.imageCacheControllerPathPatterns
        )
            ? options.imageCacheControllerPathPatterns
            : DEFAULT_IMAGE_CACHE_CONTROLLER_PATH_PATTERNS;

        this.placeholderOptions = this.normalizePlaceholderOptions(options);
    }

    private normalizeCdnPrefixes(cdnPrefixes: string[] | undefined): string[] {
        if (!Array.isArray(cdnPrefixes) || !cdnPrefixes.length) {
            return ['/'];
        }

        const normalized = cdnPrefixes
            .map((prefix) => String(prefix || '').trim())
            .filter((prefix) => Boolean(prefix));

        return normalized.length ? Array.from(new Set(normalized)) : ['/'];
    }

    private normalizeFormat(format: string | false | undefined): string {
        if (format === false) return '';
        return String(format ?? '').trim();
    }

    private normalizeQuality(
        quality: string | number | undefined,
        fallback: string | number | ''
    ): string | number | '' {
        if (
            quality !== undefined &&
            quality !== null &&
            String(quality) !== ''
        ) {
            return quality;
        }

        return fallback;
    }

    private normalizeVersion(
        version: string | number | boolean | undefined,
        fallback: string | number | boolean | ''
    ): string | number | boolean | '' {
        if (version !== undefined && version !== null) {
            return version;
        }

        return fallback;
    }

    private normalizeWidth(
        width: string | number | undefined,
        fallback: string | number | ''
    ): string | number | '' {
        if (width !== undefined && width !== null && String(width) !== '') {
            return width;
        }

        return fallback;
    }

    /**
     * 正規化圖片策略設定
     *
     * LazyLoader 預設只提供原生 lazy 載入能力，不自動轉 WebP。
     * 需要 WebP、quality、version 或 CDN fallback 時，由 placeholderOptions 宣告。
     */
    private normalizePlaceholderOptions(
        options: LazyLoaderOptions
    ): PlaceholderOptions {
        const defaultCdnPrefixes = this.normalizeCdnPrefixes(
            options.cdnPrefixes
        );

        const defaultTransform =
            typeof options.transform === 'function' ? options.transform : null;

        const globalFormat = this.normalizeFormat(DEFAULT_IMAGE_FORMAT);

        const globalQuality = DEFAULT_IMAGE_QUALITY;

        const globalVersion = DEFAULT_IMAGE_VERSION;

        const globalWidth = DEFAULT_IMAGE_WIDTH;

        const rawPlaceholderOptions =
            options.placeholderOptions &&
            typeof options.placeholderOptions === 'object' &&
            !Array.isArray(options.placeholderOptions)
                ? options.placeholderOptions
                : {};

        const defaultPlaceholder = rawPlaceholderOptions.default || {};

        const hasDefaultTransform = Object.prototype.hasOwnProperty.call(
            defaultPlaceholder,
            'transform'
        );

        const normalizedDefault: PlaceholderTagConfig = {
            error: defaultPlaceholder.error || '/Images/ComingSoon.png',
            cdnPrefixes: Array.isArray(defaultPlaceholder.cdnPrefixes)
                ? this.normalizeCdnPrefixes(defaultPlaceholder.cdnPrefixes)
                : defaultCdnPrefixes,
            transform: hasDefaultTransform
                ? typeof defaultPlaceholder.transform === 'function'
                    ? defaultPlaceholder.transform
                    : null
                : defaultTransform,

            format: Object.prototype.hasOwnProperty.call(
                defaultPlaceholder,
                'format'
            )
                ? this.normalizeFormat(defaultPlaceholder.format)
                : globalFormat,

            quality: this.normalizeQuality(
                defaultPlaceholder.quality,
                globalQuality
            ),

            version: this.normalizeVersion(
                defaultPlaceholder.version,
                globalVersion
            ),

            width: this.normalizeWidth(defaultPlaceholder.width, globalWidth),
        };

        const normalized: PlaceholderOptions = {
            default: normalizedDefault,
        };

        Object.keys(rawPlaceholderOptions).forEach((tag) => {
            if (!tag || tag === 'default') return;

            const current = rawPlaceholderOptions[tag] || {};

            const hasTagTransform = Object.prototype.hasOwnProperty.call(
                current,
                'transform'
            );

            normalized[tag] = {
                error: current.error || normalized.default.error,
                cdnPrefixes: Array.isArray(current.cdnPrefixes)
                    ? this.normalizeCdnPrefixes(current.cdnPrefixes)
                    : normalized.default.cdnPrefixes,
                transform: hasTagTransform
                    ? typeof current.transform === 'function'
                        ? current.transform
                        : null
                    : normalized.default.transform,

                format: Object.prototype.hasOwnProperty.call(current, 'format')
                    ? this.normalizeFormat(current.format)
                    : normalized.default.format,

                quality: this.normalizeQuality(
                    current.quality,
                    normalized.default.quality
                ),

                version: this.normalizeVersion(
                    current.version,
                    normalized.default.version
                ),

                width: this.normalizeWidth(
                    current.width,
                    normalized.default.width
                ),
            };
        });

        return normalized;
    }

    /**
     * 正規化 directive 綁定值
     *
     * 注意：
     * - binding 沒傳 format 時，回傳 undefined，讓 tag config 決定
     * - binding 傳 format: false 或 format: '' 時，代表明確關閉 format
     */
    private normalizeBindingValue(
        bindingValue: BindingInput
    ): NormalizedBinding {
        if (
            bindingValue &&
            typeof bindingValue === 'object' &&
            !Array.isArray(bindingValue)
        ) {
            const hasFormatField = Object.prototype.hasOwnProperty.call(
                bindingValue,
                'format'
            );

            const hasQualityField = Object.prototype.hasOwnProperty.call(
                bindingValue,
                'quality'
            );

            const hasVersionField = Object.prototype.hasOwnProperty.call(
                bindingValue,
                'version'
            );

            const hasWidthField = Object.prototype.hasOwnProperty.call(
                bindingValue,
                'width'
            );

            return {
                src: String(bindingValue.src || ''),
                tag: String(bindingValue.tag || '').trim(),
                format: hasFormatField
                    ? this.normalizeFormat(bindingValue.format)
                    : undefined,
                quality: hasQualityField
                    ? this.normalizeQuality(bindingValue.quality, '')
                    : undefined,
                version: hasVersionField
                    ? this.normalizeVersion(bindingValue.version, '')
                    : undefined,
                width: hasWidthField
                    ? this.normalizeWidth(bindingValue.width, '')
                    : undefined,
                loading:
                    bindingValue.loading === IMAGE_LOADING.LAZY
                        ? IMAGE_LOADING.LAZY
                        : IMAGE_LOADING.EAGER,
                fetchPriority: bindingValue.fetchPriority,
                cdnPrefixes: Array.isArray(bindingValue.cdnPrefixes)
                    ? this.normalizeCdnPrefixes(bindingValue.cdnPrefixes)
                    : [],
            };
        }

        return {
            src: String(bindingValue || ''),
            tag: '',
            format: undefined,
            quality: undefined,
            version: undefined,
            width: undefined,
            loading: IMAGE_LOADING.EAGER,
            fetchPriority: undefined,
            cdnPrefixes: [],
        };
    }

    /**
     * 取得 tag 設定
     */
    private resolveTagOptions(tag: string): PlaceholderTagConfig {
        const normalizedTag = String(tag || '').trim();
        if (!normalizedTag) return this.placeholderOptions.default;

        return (
            this.placeholderOptions[normalizedTag] ||
            this.placeholderOptions.default
        );
    }

    /**
     * 依 cdnPrefixes 取得 resolver
     */
    private getResolverByCdnPrefixes(
        cdnPrefixes: string[]
    ): ImageCdnUrlResolver {
        const resolverKey = JSON.stringify(cdnPrefixes || []);
        const cachedResolver = this.cdnResolverMap.get(resolverKey);
        if (cachedResolver) return cachedResolver;

        const resolver = new ImageCdnUrlResolver(cdnPrefixes || []);
        this.cdnResolverMap.set(resolverKey, resolver);
        return resolver;
    }

    private applyTransform(
        imgPath: string,
        tagSettings: PlaceholderTagConfig
    ): string {
        if (!imgPath || typeof tagSettings.transform !== 'function') {
            return imgPath;
        }

        try {
            return tagSettings.transform(imgPath);
        } catch {
            return imgPath;
        }
    }

    private getImageUrlCandidates(
        imgPath: string,
        cdnPrefixes: string[],
        options: ImageCdnResolveOptions
    ): string[] {
        const resolver = this.getResolverByCdnPrefixes(cdnPrefixes);

        // 將圖片策略集中交給 resolver 組 URL，元件只需提供 src / tag
        return resolver.resolveCandidates(imgPath, {
            ...options,
            imageCacheMode: this.imageCacheMode,
            imageCacheControllerPath: this.imageCacheControllerPath,
            imageCacheControllerPathPatterns:
                this.imageCacheControllerPathPatterns,
        });
    }

    private isCurrentRequest(el: HTMLImageElement, requestId: number): boolean {
        return this.elementStateMap.get(el)?.requestId === requestId;
    }

    private setElementState(
        el: HTMLImageElement,
        state: LazyElementState
    ): void {
        this.elementStateMap.set(el, state);
    }

    /**
     * 清除圖片尚未觸發的畫面觀察器
     */
    private disconnectElementObserver(el: HTMLImageElement): void {
        const observer = this.elementObserverMap.get(el);
        if (!observer) return;

        observer.unobserve(el);
        observer.disconnect();
        this.elementObserverMap.delete(el);
    }

    /**
     * 判斷圖片是否已進入 lazy load 可讀取範圍
     */
    private isElementAlreadyInViewport(el: HTMLImageElement): boolean {
        if (typeof window === 'undefined') return true;

        const rect = el.getBoundingClientRect();
        return (
            rect.top < window.innerHeight &&
            rect.bottom > 0 &&
            rect.left < window.innerWidth &&
            rect.right > 0
        );
    }

    /**
     * 等圖片進入可讀取範圍後執行載入流程
     */
    private runWhenElementVisible(
        el: HTMLImageElement,
        requestId: number,
        callback: () => void
    ): void {
        this.disconnectElementObserver(el);

        if (
            typeof window === 'undefined' ||
            typeof IntersectionObserver === 'undefined' ||
            this.isElementAlreadyInViewport(el)
        ) {
            // 無法建立觀察器時直接執行載入流程，避免圖片停在等待狀態
            callback();
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                const isVisible =
                    entry.isIntersecting ||
                    entry.intersectionRatio > 0 ||
                    entry.boundingClientRect.top < window.innerHeight;

                // 圖片進入可讀取範圍後才放入真實 URL，避免初始化時一次觸發整頁圖片請求
                if (!isVisible) return;

                this.disconnectElementObserver(el);

                if (!this.isCurrentRequest(el, requestId)) return;

                callback();
            },
            {
                threshold: 0.1,
            }
        );

        this.elementObserverMap.set(el, observer);
        observer.observe(el);
    }

    private applyErrorFallback(
        el: HTMLImageElement,
        errorSrc: string,
        requestId: number
    ): void {
        if (!this.isCurrentRequest(el, requestId)) return;

        el.dataset.lazyState = 'error';
        el.onload = null;
        el.onerror = null;

        const currentState = this.elementStateMap.get(el);
        if (currentState) {
            this.setElementState(el, {
                ...currentState,
                finalUrl: errorSrc || currentState.finalUrl,
            });
        }

        if (errorSrc && el.src !== errorSrc) {
            el.src = errorSrc;
        }
    }

    /**
     * DOM 版本 lazy 初始化 (給非 Vue 模板使用)
     */
    private initElement(
        el: HTMLImageElement,
        bindingValue: BindingInput
    ): void {
        const normalizedBinding = this.normalizeBindingValue(bindingValue);
        const tagSettings = this.resolveTagOptions(normalizedBinding.tag);

        const cdnPrefixes = normalizedBinding.cdnPrefixes.length
            ? normalizedBinding.cdnPrefixes
            : tagSettings.cdnPrefixes;

        const sourcePath = normalizedBinding.src
            .trim()
            .replace(/^(['"])(.+)\1$/, '$2');

        const finalImgPath = this.applyTransform(
            sourcePath,
            tagSettings
        ).trim();

        if (!finalImgPath) {
            el.dataset.lazyState = 'waiting';
            this.disconnectElementObserver(el);
            this.elementStateMap.delete(el);
            return;
        }

        const imageFormat =
            normalizedBinding.format !== undefined
                ? normalizedBinding.format
                : tagSettings.format;

        const imageQuality =
            normalizedBinding.quality !== undefined
                ? normalizedBinding.quality
                : tagSettings.quality;

        const imageVersion =
            normalizedBinding.version !== undefined
                ? normalizedBinding.version
                : tagSettings.version;

        const imageWidth =
            normalizedBinding.width !== undefined
                ? normalizedBinding.width
                : tagSettings.width;

        const candidates = this.getImageUrlCandidates(
            finalImgPath,
            cdnPrefixes,
            {
                format: imageFormat || undefined,
                quality: imageQuality || undefined,
                version: imageVersion || undefined,
                width: imageWidth || undefined,
            }
        );

        const primaryUrl = candidates[0] || '';

        if (!primaryUrl) {
            const requestId = ++this.imageRequestId;
            this.setElementState(el, {
                requestId,
                sourcePath: finalImgPath,
                finalUrl: '',
                candidateIndex: 0,
            });
            this.applyErrorFallback(el, tagSettings.error, requestId);
            return;
        }

        const currentState = this.elementStateMap.get(el);

        if (
            currentState?.finalUrl === primaryUrl &&
            el.dataset.lazyState !== 'error'
        ) {
            el.loading = normalizedBinding.loading;
            el.decoding = 'async';

            if (normalizedBinding.fetchPriority) {
                el.fetchPriority = normalizedBinding.fetchPriority;
            }

            return;
        }

        this.disconnectElementObserver(el);

        const requestId = ++this.imageRequestId;
        let candidateIndex = 0;

        this.setElementState(el, {
            requestId,
            sourcePath: finalImgPath,
            finalUrl: primaryUrl,
            candidateIndex,
        });

        el.dataset.lazyState = 'loading';
        el.loading = normalizedBinding.loading;
        el.decoding = 'async';

        if (normalizedBinding.fetchPriority) {
            el.fetchPriority = normalizedBinding.fetchPriority;
        }

        const applyCandidate = (index: number): void => {
            if (!this.isCurrentRequest(el, requestId)) return;

            const candidateUrl = candidates[index]!;

            candidateIndex = index;
            this.setElementState(el, {
                requestId,
                sourcePath: finalImgPath,
                finalUrl: candidateUrl,
                candidateIndex,
            });

            // 進入 viewport 後才切換實際候選 URL，避免初始化時就觸發下載
            if (el.src !== candidateUrl) {
                el.src = candidateUrl;
            }

            if (el.complete && (el.naturalWidth > 0 || el.naturalHeight > 0)) {
                el.onload?.(new Event('load'));
            }
        };

        el.onload = () => {
            if (!this.isCurrentRequest(el, requestId)) return;

            el.dataset.lazyState = 'loaded';
        };

        el.onerror = () => {
            if (!this.isCurrentRequest(el, requestId)) return;

            if (candidateIndex + 1 < candidates.length) {
                applyCandidate(candidateIndex + 1);
                return;
            }

            this.applyErrorFallback(el, tagSettings.error, requestId);
        };

        this.runWhenElementVisible(el, requestId, () => {
            applyCandidate(0);
        });
    }

    /**
     * 註冊 Vue 指令
     */
    registerVueDirective(name = 'lazy-img'): void {
        this.directiveName = name;

        if (typeof Vue.directive !== 'function') return;

        const lazyDirective: DirectiveOptions = {
            inserted: (el: HTMLElement, binding: DirectiveBinding) => {
                if (el instanceof HTMLImageElement) {
                    this.initElement(el, binding.value as BindingInput);
                }
            },
            update: (el: HTMLElement, binding: DirectiveBinding) => {
                if (!(el instanceof HTMLImageElement)) return;

                const nextBinding = this.normalizeBindingValue(
                    binding.value as BindingInput
                );
                const prevBinding = this.normalizeBindingValue(
                    binding.oldValue as BindingInput
                );

                if (
                    nextBinding.src !== prevBinding.src ||
                    nextBinding.tag !== prevBinding.tag ||
                    nextBinding.format !== prevBinding.format ||
                    nextBinding.quality !== prevBinding.quality ||
                    nextBinding.version !== prevBinding.version ||
                    nextBinding.width !== prevBinding.width ||
                    nextBinding.loading !== prevBinding.loading ||
                    nextBinding.fetchPriority !== prevBinding.fetchPriority ||
                    nextBinding.cdnPrefixes.join('|') !==
                        prevBinding.cdnPrefixes.join('|')
                ) {
                    this.initElement(el, binding.value as BindingInput);
                }
            },
        };

        Vue.directive(name, lazyDirective);
    }

    /**
     * 手動掃描 DOM (給非 Vue 模板或動態插入 HTML 使用)
     */
    hydrate(root: ParentNode = document): void {
        if (!this.directiveName) {
            console.warn(
                'LazyLoader.hydrate(): 尚未呼叫 registerVueDirective()'
            );
            return;
        }

        const attributeName = `v-${this.directiveName}`;
        const selector = `img[${attributeName}]`;
        const lazyImgs = root.querySelectorAll(selector);

        lazyImgs.forEach((node) => {
            if (!(node instanceof HTMLImageElement)) return;

            const imgPath = node.getAttribute(attributeName) || '';
            this.initElement(node, imgPath);
            node.removeAttribute(attributeName);
        });
    }
}
